// src/api/autotaskDataApi.js
// Autotask cached data — sync, read, and export.

import apiClient from "./apiClient";

// Get sync status — shows when each entity was last synced
export async function getSyncStatus() {
  const response = await apiClient.get("/api/autotask-data/sync-status");
  return response.data;
}

// Trigger a sync — entity can be 'all', 'tickets', 'companies', 'timeEntries', 'resources'
export async function triggerSync(entity = "all") {
  // Use apiClient so the MSAL token interceptor fires correctly.
  // Override timeout per-request — full sync takes several minutes.
  const response = await apiClient.post(
    `/api/autotask-data/sync?entity=${entity}`,
    {},
    { timeout: 10 * 60 * 1000 } // 10 minutes
  );
  return response.data;
}

// Get cached data for an entity
export async function getAutotaskData(entity) {
  const response = await apiClient.get(`/api/autotask-data/${entity}`);
  return response.data;
}

// Load companies with tier data for client autocomplete and Client Register
export async function loadAtCompanies() {
  try {
    const response = await apiClient.get("/api/autotask-data/companies-with-tiers");
    const companies = response.data?.companies || [];
    console.log("companies-with-tiers response:", response.status, "count:", companies.length);
    if (companies.length > 0) return companies;
    // Fallback: try raw companies cache
    console.log("Falling back to raw companies cache...");
    const raw = await apiClient.get("/api/autotask-data/companies");
    const items = raw.data?.data || [];
    console.log("Raw companies cache:", items.length, "items");
    return items
      .filter(c => c.isActive !== false)
      .map(c => ({ id: c.id, companyName: c.companyName, _tier: "Adhoc", webAddress: c.webAddress || "" }))
      .sort((a, b) => a.companyName.localeCompare(b.companyName));
  } catch (e) {
    console.error("loadAtCompanies error:", e.message, e.response?.status, e.response?.data);
    return [];
  }
}

// Download CSV export for an entity
export async function exportCsv(entity) {
  const response = await apiClient.get(`/api/autotask-data/${entity}/export`, {
    responseType: "blob",
  });
  // Trigger browser download
  const url  = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  const date = new Date().toISOString().slice(0, 10);
  link.href  = url;
  link.setAttribute("download", `autotask-${entity}-${date}.csv`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/* ── Client tier override ────────────────────────────────────────────────────
 * ⛔ The MANUAL value wins and the Autotask sync can never clobber it: the
 * override lives in its own blob server-side and is merged into
 * `/companies-with-tiers` at read time. Companies come back with `_tier`
 * (resolved) and `_tierSource` ("manual" | "derived" | "default").
 *
 * ⚠ Send a value from the canonical enum only. These strings are LOOKUP KEYS —
 * CADENCE_TEMPLATES is keyed by exactly them, so a typo does not fail loudly,
 * it silently falls through to the Adhoc cadence. Sending "" CLEARS the
 * override and hands the client back to the contract-name derivation.
 */
export async function setClientTier(companyId, tier) {
  const res = await apiClient.put(`/api/autotask-data/client-tiers/${companyId}`, { tier });
  return res.data;                     // { ok, companyId, tier, source }
}

export async function getClientTiers() {
  const res = await apiClient.get("/api/autotask-data/client-tiers");
  return res.data;                     // { tiers: [...], overrides: { cid: tier } }
}
